import React, { useState, useEffect, useContext, useRef } from 'react';
import './UserManagement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserRow from './UserRow';
import UserUpdateModal from '../UserUpdateModal/UserUpdateModal';
import UserCreationModal from '../UserCreationModal/UserCreationModal';
import InteractiveList from '../InteractiveList/InteractiveList';
import ListUpdateMessage from '../ListUpdateMessage/ListUpdateMessage';
import Filter from '../Filter/Filter';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import baseApi from '../../common/api/base';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import logComponent from '../../common/logComponent';

const USERS_PER_LOAD = 50;

const COLUMNS = [{
  heading: 'Name',
  sortField: 'LastName',
}, {
  heading: 'Role',
}, {
  heading: 'Status'
}, {
  heading: ''
}]

export default function UserManagement({ groupsLoading, groupsError, reloadGroups }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const [visibleUser, setVisibleUser] = useState(null);
  const [creationModalIsVisible, setCreationModalIsVisible] = useState(false);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [deletedUser, setDeletedUser] = useState(null);
  const [addedUser, setAddedUser] = useState(null);
  const [trueSearchTerms, setTrueSearchTerms] = useState(null);
  const [searchTermUpdateTimeout, setSearchTermUpdateTimeout] = useState(null);
  const [users, setUsers] = useState(null);
  const [usersOnDeck, setUsersOnDeck] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const [sortDescending, setSortDescending] = useState(false);
  const [isAdminFilter, setIsAdminFilter] = useState([]);
  const [groupTypeFilter, setGroupTypeFilter] = useState([]);
  const { hasToken, activeUser: adminUser } = environmentContext;
  const { groups } = adminContext;
  const userToDisplay = updatedUser || addedUser || deletedUser;
  const userName = userToDisplay ? userToDisplay.legalFirstName + ' ' + userToDisplay.lastName : '';
  const deletedUserIsFamilyAndAdminIsNotStatus = deletedUser && deletedUser.groupTypeString === 'Family' && (adminUser.groupTypeString === 'Family' || adminUser.groupTypeString === 'Facility');
  const firstRun = useRef(true);

  if (logComponent('UserManagement')) { console.debug('UserManagement rendered'); }

  const inputSearchTermsRef = useRef('')
  const setInputSearchTermsRef = (el) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement setInputSearchTermsRef'); }
    inputSearchTermsRef.current = el;
  } 


  const mostRecentUserUpdate = useRef('')
  const setMostRecentUserUpdate = (el) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement setMostRecentUserUpdate'); }
    mostRecentUserUpdate.current = el;
  } 
  const mostRecentRequest = useRef('')
  const setMostRecentRequest = (el) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement setMostRecentRequest'); }
    mostRecentRequest.current = el;
  } 



  const updateType = userToDisplay
    ? (updatedUser
      ? 'updated'
      : (deletedUser
        ? (deletedUserIsFamilyAndAdminIsNotStatus ? 'updated and removed from the User list' : 'updated')
        : 'created'))
    : '';

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('UserManagement')) { console.debug('UserManagement onMount'); }
    if (!hasToken) return;

    if (!firstRun.current) return;
    if (adminUser.groupTypeString === 'Family') {
        loadAllUsers();
        firstRun.current = false;
    } else {
        // For non-Family users, trigger initial load with empty search to show paged list
        setTrueSearchTerms('');
        firstRun.current = false;
    }
  }, [])

  useEffect(() => {
    if (logComponent('UserManagement')) { console.debug('UserManagement useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    if (adminUser.groupTypeString === 'Family') {
      loadAllUsers();
    }
  }, [environmentContext.hasToken])

  useEffect(() => {
    if (logComponent('UserManagement')) { console.debug('UserManagement useEffect [trueSearchTerms, sortDescending, isAdminFilter.length, groupTypeFilter.length]'); }
    if (!hasToken) return;
    loadUsers();
  }, [trueSearchTerms, sortDescending, isAdminFilter.length, groupTypeFilter.length])

  const loadAllUsers = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement loadAllUsers'); }
    if (!hasToken) return;

    setUsers(null);
    setUsersLoading(true);
    setUsersError(false);

    baseApi.User.GetManagedUsers()
      .then(
        result => {
          setUsers(result.data);
          setUsersLoading(false);
          setUsersError(false);
        },
        error => {
          setUsers(null);
          setUsersLoading(false);
          setUsersError(true);
        }
      )
  }

  const loadUsers = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement loadUsers'); }
    if (!hasToken) return;

    // Allow empty search terms for initial load (paged list)
    // Only require 3+ characters if search terms were explicitly set by user and are non-empty
    const searchTerm = trueSearchTerms || '';
    if (searchTerm.length > 0 && searchTerm.length < 3) {
      // Don't clear users - keep the initial list visible until 3+ characters are typed
      return;
    }

    let timestamp = Date.now();

    setUsers(null);
    setUsersOnDeck(null);
    setUsersLoading(true);
    setUsersError(false);
    setMostRecentRequest(timestamp);

    const queryObject = {
      SearchValues: searchTerm,
      SortDesc: sortDescending,
      NumberToSkip: 0,
      NumberToTake: USERS_PER_LOAD * 2,
      FilterBy: {
        GroupTypeEnum: adminUser.groupTypeString === 'Corporate' ? ['Corporate'] : groupTypeFilter,
        IsAdmin: isAdminFilter
      }
    }

    baseApi.User.GetUsersForCurrentUser(queryObject)
      .then(
        result => {
          if (timestamp !== mostRecentRequest.current) return;

          setUsers(result.data.filter((user, index) => index < USERS_PER_LOAD));
          setUsersOnDeck(result.data.filter((user, index) => index >= USERS_PER_LOAD));
          setUsersLoading(false);
          setUsersError(false);
          setMostRecentRequest(null);

        },
        error => {
          if (timestamp !== mostRecentRequest.current) return;

          setUsers(null);
          setUsersOnDeck(null);
          setUsersLoading(false);
          setUsersError(true);
          setMostRecentRequest(null);
        }
      )
  }

  const loadMoreUsers = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement loadMoreUsers'); }
    if (!hasToken) return;

    const moreUsersToLoad = usersOnDeck.length >= USERS_PER_LOAD;
    const totalUsersLoaded = users.length + usersOnDeck.length;

    const newUsersToShow = [...users, ...usersOnDeck];

    // Add users on deck to users
    setUsers(newUsersToShow);
    setUsersOnDeck(null);

    // If there are no more devices to load, bail
    if (!moreUsersToLoad) return;

    const queryObject = {
      SearchValues: trueSearchTerms || '',
      SortDesc: sortDescending,
      NumberToSkip: totalUsersLoaded,
      NumberToTake: USERS_PER_LOAD,
      FilterBy: {
        GroupTypeEnum: groupTypeFilter,
        IsAdmin: isAdminFilter
      }
    }

    // Otherwise fetch more devices
    baseApi.User.GetUsersForCurrentUser(queryObject)
      .then(
        result => {
          setUsersOnDeck(result.data);
        },
        error => {
          setUsersOnDeck(null);
        })
  }

  const addUser = (user) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement addUser'); }
    const prevUsers = users ? users : [];
    setUsers([user, ...prevUsers]);
    setUpdatedUser(null);
    setAddedUser(user);
    setDeletedUser(null);
    setMostRecentUserUpdate(Date.now());
  }

  const updateUser = (updatedUser) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement updateUser'); }
    setUsers(users.map(user => user.userId === updatedUser.userId ? updatedUser : user));
    setUpdatedUser(updatedUser);
    setAddedUser(null);
    setDeletedUser(null);
    setMostRecentUserUpdate(Date.now());
  }

  const deleteUser = (deletedUser) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement deleteUser'); }
    setUsers(users.filter(user => user.userId !== deletedUser.userId));
    setUpdatedUser(null);
    setAddedUser(null);
    setDeletedUser(deletedUser);
    setMostRecentUserUpdate(Date.now());
  }

  const showUserCreationModal = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement showUserCreationModal'); }
    setVisibleUser(null);
    setCreationModalIsVisible(true);

  }

  const hideUserCreationModal = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement hideUserCreationModal'); }
    setCreationModalIsVisible(false);
  }

  const hideVisibleUser = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement hideVisibleUser'); }
    setVisibleUser(null);
  }

  const toggleVisibleUser = (userToToggle) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement toggleVisibleUser'); }
    let newVisibleUser = null;

    if (visibleUser !== userToToggle) {
      newVisibleUser = userToToggle;
    }
    setVisibleUser(newVisibleUser);
  }

  const getGroupTypeOptions = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement getGroupTypeOptions'); }

    let options = [];

    /* eslint-disable */
    switch (adminUser.groupTypeString) {
      case 'Status Solutions':
        options = [
          {
            text: 'Corporate',
            criteria: [{
              field: 'GroupTypeString',
              acceptedValues: ['Corporate']
            }]
          }, {
            text: 'Facility',
            criteria: [{
              field: 'GroupTypeString',
              acceptedValues: ['Facility']
            }]
          }, {
            text: 'Family',
            criteria: [{
              field: 'GroupTypeString',
              acceptedValues: ['Family']
            }]
          }, {
            text: 'Status Solutions',
            criteria: [{
              field: 'GroupTypeString',
              acceptedValues: ['StatusSolutions']
            }]
          }
        ];
        break;
      case 'Corporate':
        options.push({
          text: 'Corporate',
          criteria: [{
            field: 'GroupTypeString',
            acceptedValues: ['Corporate']
          }]
        });
      case 'Facility':
        options.push({
          text: 'Facility',
          criteria: [{
            field: 'GroupTypeString',
            acceptedValues: ['Facility']
          }]
        }, {
          text: 'Family',
          criteria: [{
            field: 'GroupTypeString',
            acceptedValues: ['Family']
          }]
        });
      default:
        break;
    }
    /* eslint-enable */

    return options;
  }

  const updateSearchTerms = (e) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement updateSearchTerms'); }
    if (searchTermUpdateTimeout) clearTimeout(searchTermUpdateTimeout);

    setInputSearchTermsRef(e.target.value);
    setSearchTermUpdateTimeout(setTimeout(updateTrueSearchTerms, 350));
  }

  const updateTrueSearchTerms = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement updateTrueSearchTerms'); }
    setTrueSearchTerms(inputSearchTermsRef.current);
    setSearchTermUpdateTimeout(null);
  }

  const updateFilters = (update) => {
    if (logComponent('UserManagement')) { console.debug('UserManagement updateFilters'); }
    if (update.name === 'IsAdmin') {

      // Back-end does not handle being passed both 'true' and 'false',
      // so if both are checked, just send an empty array
      let values = update.criteria.reduce((prev, curr) => { return [...prev, ...curr.acceptedValues] }, []);
      if (values.length === 2) values = [];
      setIsAdminFilter(values);
    } else if (update.name === 'GroupTypeEnum') {
      setGroupTypeFilter(update.criteria.reduce((prev, curr) => { return [...prev, ...curr.acceptedValues] }, []));
    }
  }

  const updateSort = () => {
    if (logComponent('UserManagement')) { console.debug('UserManagement updateSort'); }
    setSortDescending(!sortDescending);
  }

  return (
    <div className="User-Management">
      {
        userToDisplay &&
        <div className="User-Management__update-message-wrapper">
          <ListUpdateMessage
            message={`${userName} has been successfully ${updateType}`}
              lastUpdate={mostRecentUserUpdate.current} />
        </div>
      }

      {
        adminUser.groupTypeString === 'Family' &&
        <InteractiveList
          columns={[
            { name: 'Name', sortField: 'LastName' },
            { name: 'Role', hideAt: 'sm', style: { width: '15%' } },
            { name: 'Status', hideAt: 'sm', style: { width: '15%' } },
            { name: '', style: { width: '66px' } }
          ]}
          defaultSortField="LastName"
          searchTextFields={['emailAddress', 'preferredFirstName', 'legalFirstName', 'lastName', 'phoneNumber']}
          searchPlaceholder="Search users"
          filters={[
            {
              title: 'Access level',
              type: 'checkbox',
              name: 'accessLevel',
              options: [
                {
                  text: 'Admin',
                  criteria: [{
                    field: 'IsAdmin',
                    acceptedValues: [true]
                  }]
                },
                {
                  text: 'Standard',
                  criteria: [{
                    field: 'IsAdmin',
                    acceptedValues: [false]
                  }]
                }
              ]
            }
          ]}
          singularItemTerm="user"
          pluralItemTerm="users"
          items={users}
          itemsLoading={usersLoading}
          itemsError={usersError}
          rowComponent={UserRow}
          showDetail={toggleVisibleUser}
          keyField="userId"  >

          <button
            className="User-Management__add-user-button"
            onClick={showUserCreationModal}>
            <FontAwesomeIcon
              className="User-Management__add-user-button-icon"
              icon="plus" />Add new user
          </button>
          {/* NO LONGER NEEDED B/C FAMILY ADMINS DON'T DEAL WITH CORRESPONDENCE 
        <p className="User-Management__legend">
          <FontAwesomeIcon 
            className="User-Management__legend-icon"
            icon="comment" />
          Indicates individual is enabled for correspondence override
        </p>
        */}
        </InteractiveList>
      }

      {/* TABLE THAT WILL REPLACE INTERACTIVE LIST */}
      {
        adminUser.groupTypeString !== 'Family' &&
        <div className="User-Management__main">
          <div className="User-Management__filter-row">
            <div className="User-Management__search-input-wrapper">
              <FontAwesomeIcon icon="search" className="User-Management__search-icon" />
              <input
                type="text"
                className="User-Management__search-input"
                placeholder="Search users"
                value={inputSearchTermsRef.current}
                onChange={updateSearchTerms} />
            </div>

            <div className="User-Management__filter-wrapper">
              <Filter
                filters={[
                  {
                    title: 'Access level',
                    type: 'checkbox',
                    name: 'IsAdmin',
                    options: [
                      {
                        text: 'Admin',
                        criteria: [{
                          field: 'IsAdmin',
                          acceptedValues: [true]
                        }]
                      },
                      {
                        text: 'Standard',
                        criteria: [{
                          field: 'IsAdmin',
                          acceptedValues: [false]
                        }]
                      }
                    ]
                  },
                  {
                    hide: adminUser.groupTypeString === 'Family' || adminUser.groupTypeString === 'Corporate',
                    title: 'User type',
                    type: 'checkbox',
                    name: 'GroupTypeEnum',
                    options: getGroupTypeOptions()
                  }
                ]}
                onChange={updateFilters}
              />
            </div>
          </div>

          <button
            className="User-Management__add-user-button"
            onClick={showUserCreationModal}>
            <FontAwesomeIcon
              className="User-Management__add-user-button-icon"
              icon="plus" />Add new user
          </button>
          {
            (
              adminUser.groupTypeString === 'Status Solutions' ||
              adminUser.groupTypeString === 'Corporate' ||
              adminUser.groupTypeString === 'Facility'
            ) &&
            <div className="User-Management__legend">
              {
                (
                  adminUser.groupTypeString === 'Status Solutions' ||
                  adminUser.groupTypeString === 'Facility'
                ) &&
                <p className="User-Management__legend-row">
                  <FontAwesomeIcon
                    className="User-Management__legend-icon"
                    icon="comment" />
                  Indicates individual is enabled for correspondence override
                </p>
              }
              {
                (
                  adminUser.groupTypeString === 'Corporate' ||
                  adminUser.groupTypeString === 'Status Solutions'
                ) &&
                <p className="User-Management__legend-row">
                  <FontAwesomeIcon
                    className="User-Management__legend-icon"
                    icon="eye" />
                  Indicates individual is enabled for mimic override
                </p>
              }
            </div>
          }
          <div className="User-Management__user-list">
            {
              <table className="User-Management__user-table">
                <thead>
                  <tr>
                    {
                      COLUMNS.map(column =>
                        <th
                          key={column.heading}
                          className={`User-Management__${column.heading.toLowerCase()}-heading`}>
                          {
                            column.sortField ?
                              <button
                                className="User-Management__column-heading User-Management__column-heading--selected"
                                onClick={updateSort}
                                data-column={column.sortField}>
                                {column.heading}
                                {
                                  <FontAwesomeIcon
                                    icon={(sortDescending ? 'arrow-down' : 'arrow-up')}
                                    className="User-Management__sort-icon" />
                                }
                              </button> :
                              <p className="User-Management__column-heading">{column.heading}</p>
                          }
                        </th>
                      )
                    }
                  </tr>
                </thead>
                <tbody>
                  {
                    users &&
                    users.length > 0 &&
                    users.map(user =>
                      <UserRow
                        key={user.userId}
                        showDetail={toggleVisibleUser}
                        user={user} />
                    )
                  }
                </tbody>
              </table>
            }

            {
              !users &&
              (usersLoading ?
                <ModuleLoadingStatus isLoading={usersLoading} isError={usersError} reloadAction={loadUsers} /> :
                <p className="User-Management__instruction-message">To search users, please enter a user name, email, or phone number above.</p>)
            }
            {
              users && users.length === 0 &&
              <p className="User-Management__empty-message">No users found...</p>
            }
          </div>
          {
            usersOnDeck &&
            usersOnDeck.length > 0 &&
            <button
              className="User-Management__load-more-button"
              onClick={loadMoreUsers}>Load more</button>
          }
        </div>
      }

      {
        visibleUser &&
        <UserUpdateModal
          adminUser={adminUser}
          user={visibleUser}
          deleteUser={deleteUser}
          updateUser={updateUser}
          hide={hideVisibleUser}
          groups={groups}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          reloadGroups={reloadGroups}
          isVisible={!!visibleUser} />
      }
      {
        creationModalIsVisible &&
        <UserCreationModal
          adminUser={adminUser}
          addUser={addUser}
          groups={groups}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          reloadGroups={reloadGroups}
          isVisible={creationModalIsVisible}
          hide={hideUserCreationModal} />
      }
    </div>
  );
}